import React from 'react';
import styled from 'styled-components';

import {alphaWhite} from '../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {globalNamespace} from '../../../helpers/models/namespaceHelpers';
import {AccountCell} from './cells/accountCell';
import {AvatarCell} from './cells/avatarCell';
import {CardCell} from './cells/cardCell';
import {CardGroupCell} from './cells/cardGroupCell';
import {CustomFieldCell} from './cells/customFieldCell';
import {DateCell} from './cells/dateCell';
import {HandleCell} from './cells/handleCell';
import {IconCell} from './cells/iconCell';
import {InboxCell} from './cells/inboxCell';
import {NamespaceCell} from './cells/namespaceCell';
import {NumberCell} from './cells/numberCell';
import {TeammateCell} from './cells/teammateCell';
import {TextCell} from './cells/textCell';
import {VirtualTableColumnCellsEnum} from './helpers/tableHelpers';
import {TableColumn} from './tableColumn';
import {TableRow, TableRowRenderOptions} from './tableRow';

/*
 * Types.
 */

export interface TableLoadingRowsProps {
  columns: ReadonlyArray<TableColumn<unknown>>;
  totalLoadingRows: number;
  size?: VisualSizesEnum;
  options: TableRowRenderOptions;
}

/*
 * Styles.
 */

const StyledLoadingRowsContainerDiv = styled.div`
  position: relative;
  display: flex;
  flex-flow: column;
  padding-top: 8px;
`;

const StyledGradientDiv = styled.div`
  position: absolute;
  background: linear-gradient(180deg, ${(p) => p.theme.foreground} 0%, ${alphaWhite(0)} 100%);
  transform: rotate(180deg);

  top: 25%;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
`;

interface StyledTableLoadingRowProps {
  $minHeight: number;
}
const StyledTableLoadingRow = styled(TableRow)<StyledTableLoadingRowProps>`
  min-height: ${(p) => p.$minHeight}px;
`;

/*
 * Component.
 */

export const TableLoadingRows = (props: TableLoadingRowsProps) => {
  const {columns, totalLoadingRows, size, options} = props;

  const cellRenderer = (index: number, column: TableColumn<any>, shouldShowStickyGradient: boolean) => {
    switch (column.cellType) {
      case VirtualTableColumnCellsEnum.ACCOUNT:
        return (
          <AccountCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            avatarSize={column.settings?.loading?.avatarSize}
            settings={column.settings}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case VirtualTableColumnCellsEnum.AVATAR:
        return (
          <AvatarCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            avatarSize={column.settings?.loading?.avatarSize}
            settings={column.settings}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case VirtualTableColumnCellsEnum.CARD:
        return (
          <CardCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            avatarSize={column.settings?.loading?.avatarSize}
            settings={column.settings}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case VirtualTableColumnCellsEnum.CUSTOM_FIELD:
        return (
          <CustomFieldCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            avatarSize={column.settings?.loading?.avatarSize}
            settings={column.settings}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case VirtualTableColumnCellsEnum.ICON:
        return (
          <IconCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            iconSize={column.settings?.loading?.iconSize}
            settings={column.settings}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case VirtualTableColumnCellsEnum.NAMESPACE:
        return (
          <NamespaceCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            value={globalNamespace}
            settings={column.settings}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case VirtualTableColumnCellsEnum.DATE:
        return (
          <DateCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            settings={column.settings}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case VirtualTableColumnCellsEnum.INBOX:
        return (
          <InboxCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            settings={column.settings}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case VirtualTableColumnCellsEnum.HANDLE:
        return (
          <HandleCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            settings={column.settings}
            index={index}
            hasChannel={false}
            onComposeMessage={() => {}}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case VirtualTableColumnCellsEnum.TEAMMATE:
        return (
          <TeammateCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            avatarSize={column.settings?.loading?.avatarSize}
            settings={column.settings}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case VirtualTableColumnCellsEnum.TEXT:
        return (
          <TextCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            settings={column.settings}
            index={index}
          />
        );
      case VirtualTableColumnCellsEnum.NUMBER:
        return (
          <NumberCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            settings={column.settings}
            index={index}
          />
        );
      case VirtualTableColumnCellsEnum.CARD_GROUP:
        return (
          <CardGroupCell
            key={`loading-cell-${index}`}
            isLoading
            size={size}
            settings={column.settings}
            index={index}
          />
        );
      default: {
        reportToBugsnag(new Error('Virtual Table Error: Missing loading cell definition.'));
        return (
          <TextCell
            key={`loading-cell-${index}`}
            size={size}
            settings={column.settings}
            index={index}
            value="Missing loading cell"
          />
        );
      }
    }
  };

  const rows = [];
  for (let i = 0; i < totalLoadingRows; i++) {
    rows.push(
      <StyledTableLoadingRow
        key={`loading-row-${i}`}
        renderColumn={() =>
          columns.map((column, index) =>
            cellRenderer(index, column, Boolean(options.shouldShowStickyGradient)),
          )
        }
        $minHeight={options.tableRowHeight}
        shouldShowRightGradient={options.shouldShowRightGradient}
        shouldAllowMultiSelect={options.shouldAllowMultiSelect}
      />,
    );
  }

  return (
    <StyledLoadingRowsContainerDiv>
      {rows}
      <StyledGradientDiv />
    </StyledLoadingRowsContainerDiv>
  );
};
